/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { Box, Button, CircularProgress } from '@mui/material';

import { HUChip } from '../../../../elements/HUChip/HUChip';
import { Namespaces } from '../../../../i18n';
import { SupportJiraApiService } from '../../../../services/SupportJiraApiService';
import { useJiraSupportStyles } from '../styles';

interface FileStatus {
  id: string;
  filename: string;
  size: number;
  blob: File;
  attachmentId?: string;
}

interface JiraFileUploadProps {
  id: string;
  name: string;
  handleChange: (attachmentIds: string[]) => void;
}

export const JiraFileUpload = memo((props: JiraFileUploadProps) => {
  const classes = useJiraSupportStyles();
  const { t } = useTranslation([Namespaces.Common, Namespaces.General]);
  const getKey = (key: string) => `${Namespaces.General}:jira_support.${key}`;

  const [files, setFiles] = useState<FileStatus[]>([]);

  const handleChange = useCallback(
    (setCallback: (prevFiles: FileStatus[]) => FileStatus[]) => {
      setFiles(files => {
        const newFiles = setCallback(files);
        props.handleChange(newFiles.map(file => file.attachmentId).filter(file => file) as string[]);
        return newFiles;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFiles],
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
      <Button className={classes.uploadFile} variant="outlined">
        <label htmlFor={props.id}>{t(getKey('fileUpload'))}</label>
        <input
          id={props.id}
          name={props.name}
          type="file"
          multiple
          className={classes.fileChooser}
          onChange={event => {
            const files: FileStatus[] = (event.target.files ? Array.from(event.target.files) : []).map(file => ({
              id: v4(),
              filename: file.name,
              size: file.size,
              blob: file,
            }));

            if (files.length) {
              handleChange(prevFiles => [...prevFiles, ...files]);
            }

            files.forEach(file => {
              const fileReader = new FileReader();
              fileReader.onload = () => {
                if (fileReader.readyState === 2) {
                  const formData = new FormData();
                  formData.append('attachment', new Blob([fileReader.result as ArrayBuffer]), file.filename);

                  void SupportJiraApiService.createAttachment({
                    data: formData,
                  }).then(data => {
                    handleChange(files =>
                      files.map(f => {
                        if (f.id === file.id) {
                          f.attachmentId = data.data.payload.attachmentId as string;
                        }
                        return f;
                      }),
                    );
                  });
                }
              };

              fileReader.readAsArrayBuffer(file.blob);
            });
          }}
        />
      </Button>
      <Box style={{ display: 'flex', margin: '8px', gap: '8px', flexWrap: 'wrap' }}>
        {files.map(file => (
          <HUChip
            key={file.id}
            label={file.filename}
            color="#f16623"
            icon={!file.attachmentId ? <CircularProgress color="secondary" size="20px" /> : undefined}
            onDelete={() => handleChange(files => files.filter(f => f.id !== file.id))}
          />
        ))}
      </Box>
    </Box>
  );
});
